import { useState, useEffect } from 'react';
import { Settings, Save, Building2, User, Palette, Image as ImageIcon, MapPin, FileText, Pencil, X, Lock, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useMunicipalConfig } from './hooks/useMunicipalConfig';
import './configuracoes-municipal.css';

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

const INITIAL = {
  nomeOficial: '', slogan: '', nomePrefeito: '', cargoPrefeito: '',
  nomeVicePrefeito: '', nomeSecretario: '', logoUrl: '', fotoPrefeito: '',
  corPrimaria: '#1e40af', corSecundaria: '#059669',
  siteOficial: '', telefone: '', cnpj: '', emailInstitucional: '',
  endereco: '', bairro: '', cep: '', cidade: '', uf: '',
};

export function ConfiguracoesMunicipal() {
  const { config, isLoading, update } = useMunicipalConfig();
  const [form, setForm] = useState({ ...INITIAL });
  const [editing, setEditing] = useState(false);
  const [snapshot, setSnapshot] = useState({ ...INITIAL });

  useEffect(() => {
    if (config) {
      const merged: any = { ...INITIAL };
      for (const key of Object.keys(INITIAL)) {
        if ((config as any)[key]) merged[key] = (config as any)[key];
      }
      setForm(merged);
      setSnapshot(merged);
    }
  }, [config]);

  const f = (key: string) => (form as any)[key] || '';
  const set = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }));

  const handleEdit = () => {
    setSnapshot({ ...form });
    setEditing(true);
  };

  const handleCancel = () => {
    setForm({ ...snapshot });
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      await update.mutateAsync(form);
      setSnapshot({ ...form });
      setEditing(false);
      toast.success('Configuracoes salvas com sucesso!');
    } catch {
      toast.error('Erro ao salvar configuracoes.');
    }
  };

  const handleImageUpload = (key: string) => {
    if (!editing) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { toast.error('Imagem deve ter no maximo 2MB.'); return; }
      const reader = new FileReader();
      reader.onload = () => set(key, reader.result as string);
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const [cnpjLoading, setCnpjLoading] = useState(false);

  const maskCnpj = (raw: string) => {
    const d = raw.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  };

  const handleCnpjChange = (val: string) => set('cnpj', maskCnpj(val));

  const buscarCnpj = async () => {
    if (!editing) return;
    const digits = form.cnpj.replace(/\D/g, '');
    if (digits.length !== 14) { toast.error('CNPJ deve ter 14 digitos.'); return; }
    setCnpjLoading(true);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${digits}`);
      if (!res.ok) throw new Error('CNPJ nao encontrado');
      const d = await res.json();
      const updates: Record<string, string> = {};
      if (d.razao_social) updates.nomeOficial = d.razao_social;
      if (d.ddd_telefone_1) updates.telefone = `(${d.ddd_telefone_1.slice(0, 2)}) ${d.ddd_telefone_1.slice(2)}`;
      if (d.email) updates.emailInstitucional = d.email;
      if (d.logradouro) updates.endereco = d.numero ? `${d.logradouro}, ${d.numero}` : d.logradouro;
      if (d.bairro) updates.bairro = d.bairro;
      if (d.cep) updates.cep = d.cep.replace(/(\d{5})(\d{3})/, '$1-$2');
      if (d.municipio) updates.cidade = d.municipio;
      if (d.uf) updates.uf = d.uf;
      setForm(prev => ({ ...prev, ...updates }));
      toast.success(`Dados de "${d.razao_social || 'CNPJ'}" carregados!`);
    } catch {
      toast.error('Nao foi possivel buscar o CNPJ. Verifique o numero.');
    }
    setCnpjLoading(false);
  };

  if (isLoading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>;

  const disabled = !editing;

  return (
    <div className="admin-page animate-fadeIn cfg-page">
      {/* ═══ HERO STRIP — Configurações do Município ═══ */}
      <div className="cfg-hero">
        <div className="cfg-hero-circle c1" />
        <div className="cfg-hero-circle c2" />
        <div className="cfg-hero-content">
          <div style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="cfg-hero-badge">
              <Settings size={10} /> GESTÃO INSTITUCIONAL MUNICIPAL
            </div>
            <h1 className="cfg-hero-title">Configurações do Município</h1>
            <p className="cfg-hero-subtitle">
              Identidade institucional, branding, gestão executiva e dados oficiais da prefeitura.
            </p>
          </div>

          <div className="cfg-hero-actions">
            {!editing ? (
              <button
                type="button"
                className="cfg-hero-edit-btn"
                onClick={handleEdit}
              >
                <Pencil size={13} /> Editar configurações
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="cfg-hero-cancel-btn"
                  onClick={handleCancel}
                >
                  <X size={13} /> Cancelar
                </button>
                <button
                  type="button"
                  className="cfg-hero-save-btn"
                  onClick={() => void handleSave()}
                  disabled={update.isPending}
                >
                  <Save size={13} /> {update.isPending ? 'Salvando...' : 'Salvar alterações'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Status bar ─── */}
      <div className={`cfg-status-bar ${editing ? 'cfg-status-editing' : ''}`}>
        {editing ? (
          <><Pencil size={12} /> Modo edicao ativo — os campos estao desbloqueados</>
        ) : (
          <><Lock size={12} /> Campos protegidos — clique em "Editar" para alterar</>
        )}
      </div>

      {/* ─── 3-column grid ─── */}
      <div className="cfg-grid-3">

        {/* COL 1: Identidade */}
        <div className="cfg-card">
          <h3 className="cfg-card-title"><Building2 size={13} /> Identidade</h3>
          <div className="cfg-field">
            <label>CNPJ</label>
            <div className="cfg-cnpj-row">
              <input value={f('cnpj')} onChange={e => handleCnpjChange(e.target.value)} placeholder="00.000.000/0001-00" className="cfg-input" disabled={disabled} style={{ flex: 1 }} />
              {editing && (
                <button type="button" className="cfg-cnpj-btn" onClick={() => void buscarCnpj()} disabled={cnpjLoading}>
                  {cnpjLoading ? <><span className="cfg-spinner" /> Buscando...</> : <><FileText size={11} /> Buscar</>}
                </button>
              )}
            </div>
          </div>
          <div className="cfg-field">
            <label>Razao Social</label>
            <input value={f('nomeOficial')} onChange={e => set('nomeOficial', e.target.value)} placeholder="Prefeitura Municipal de..." className="cfg-input" disabled={disabled} />
          </div>
          <div className="cfg-field">
            <label>Slogan</label>
            <input value={f('slogan')} onChange={e => set('slogan', e.target.value)} placeholder="Trabalho Serio, Futuro Certo" className="cfg-input" disabled={disabled} />
          </div>
          <div className="cfg-row">
            <div className="cfg-field">
              <label>Telefone</label>
              <input value={f('telefone')} onChange={e => set('telefone', e.target.value)} placeholder="(xx) xxxx-xxxx" className="cfg-input" disabled={disabled} />
            </div>
            <div className="cfg-field">
              <label>Email</label>
              <input value={f('emailInstitucional')} onChange={e => set('emailInstitucional', e.target.value)} placeholder="contato@cidade.gov.br" className="cfg-input" disabled={disabled} />
            </div>
          </div>
          <div className="cfg-field">
            <label>Site Oficial</label>
            <input value={f('siteOficial')} onChange={e => set('siteOficial', e.target.value)} placeholder="www.cidade.gov.br" className="cfg-input" disabled={disabled} />
          </div>
        </div>

        {/* COL 2: Gestão + Endereço */}
        <div className="cfg-col-stack">
          <div className="cfg-card">
            <h3 className="cfg-card-title"><User size={13} /> Gestao</h3>
            <div className="cfg-field">
              <label>Prefeito(a)</label>
              <input value={f('nomePrefeito')} onChange={e => set('nomePrefeito', e.target.value)} placeholder="Nome completo" className="cfg-input" disabled={disabled} />
            </div>
            <div className="cfg-row">
              <div className="cfg-field">
                <label>Cargo</label>
                <input value={f('cargoPrefeito')} onChange={e => set('cargoPrefeito', e.target.value)} placeholder="Prefeito" className="cfg-input" disabled={disabled} />
              </div>
              <div className="cfg-field">
                <label>Vice</label>
                <input value={f('nomeVicePrefeito')} onChange={e => set('nomeVicePrefeito', e.target.value)} placeholder="Nome do vice" className="cfg-input" disabled={disabled} />
              </div>
            </div>
            <div className="cfg-field">
              <label>Secretario(a) Educacao</label>
              <input value={f('nomeSecretario')} onChange={e => set('nomeSecretario', e.target.value)} placeholder="Nome do(a) secretario(a)" className="cfg-input" disabled={disabled} />
            </div>
          </div>

          <div className="cfg-card">
            <h3 className="cfg-card-title"><MapPin size={13} /> Endereco</h3>
            <div className="cfg-field">
              <label>Logradouro</label>
              <input value={f('endereco')} onChange={e => set('endereco', e.target.value)} placeholder="Rua / Av., numero" className="cfg-input" disabled={disabled} />
            </div>
            <div className="cfg-row cfg-row-3">
              <div className="cfg-field">
                <label>Bairro</label>
                <input value={f('bairro')} onChange={e => set('bairro', e.target.value)} placeholder="Centro" className="cfg-input" disabled={disabled} />
              </div>
              <div className="cfg-field">
                <label>CEP</label>
                <input value={f('cep')} onChange={e => set('cep', e.target.value)} placeholder="00000-000" className="cfg-input" disabled={disabled} />
              </div>
            </div>
            <div className="cfg-row cfg-row-3">
              <div className="cfg-field">
                <label>Cidade</label>
                <input value={f('cidade')} onChange={e => set('cidade', e.target.value)} placeholder="Nome da cidade" className="cfg-input" disabled={disabled} />
              </div>
              <div className="cfg-field">
                <label>UF</label>
                <select value={f('uf')} onChange={e => set('uf', e.target.value)} className="cfg-input" disabled={disabled}>
                  <option value="">--</option>
                  {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* COL 3: Branding */}
        <div className="cfg-card">
          <h3 className="cfg-card-title"><ImageIcon size={13} /> Branding Visual</h3>
          <div className="cfg-row">
            <div className={`cfg-upload-area ${disabled ? 'cfg-upload-locked' : ''}`} onClick={() => handleImageUpload('logoUrl')}>
              {form.logoUrl ? <img src={form.logoUrl} alt="Logo" className="cfg-upload-preview" /> : <><Building2 size={18} /><span>Logo / Brasao</span></>}
            </div>
            <div className={`cfg-upload-area ${disabled ? 'cfg-upload-locked' : ''}`} onClick={() => handleImageUpload('fotoPrefeito')}>
              {form.fotoPrefeito ? <img src={form.fotoPrefeito} alt="Prefeito" className="cfg-upload-preview" /> : <><User size={18} /><span>Foto Prefeito</span></>}
            </div>
          </div>

          <div className="cfg-divider" />

          <h4 className="cfg-section-sub"><Palette size={12} /> Cores Institucionais</h4>
          <div className="cfg-row">
            <div className="cfg-field">
              <label>Primaria</label>
              <div className="cfg-color-row">
                <input type="color" value={form.corPrimaria} onChange={e => set('corPrimaria', e.target.value)} className="cfg-color-picker" disabled={disabled} />
                <input value={form.corPrimaria} onChange={e => set('corPrimaria', e.target.value)} className="cfg-input cfg-color-text" disabled={disabled} />
              </div>
            </div>
            <div className="cfg-field">
              <label>Secundaria</label>
              <div className="cfg-color-row">
                <input type="color" value={form.corSecundaria} onChange={e => set('corSecundaria', e.target.value)} className="cfg-color-picker" disabled={disabled} />
                <input value={form.corSecundaria} onChange={e => set('corSecundaria', e.target.value)} className="cfg-input cfg-color-text" disabled={disabled} />
              </div>
            </div>
          </div>
          <div className="cfg-preview-strip" style={{ background: `linear-gradient(135deg, ${form.corPrimaria} 0%, ${form.corSecundaria} 100%)` }}>
            <span>{form.nomeOficial || config?.nome || 'Prefeitura Municipal'}</span>
            <span style={{ opacity: .7, fontSize: '.55rem' }}>{form.slogan || 'Slogan da prefeitura'}</span>
          </div>

          <div className="cfg-divider" />

          <div className="cfg-security-note">
            <Shield size={12} />
            <span>Alteracoes nestas configuracoes afetam todos os comunicados e documentos oficiais do municipio.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
